import { MemberProfile, ActivityItem, recentActivity } from '../../../mocks/dashboard';

const ACTIVITY_ICONS: Record<ActivityItem['type'], string> = {
  saved:   'ri-bookmark-line',
  alert:   'ri-notification-3-line',
  download:'ri-download-2-line',
  viewed:  'ri-eye-line',
  account: 'ri-settings-3-line',
  bid:     'ri-auction-line',
};
const ACTIVITY_COLORS: Record<ActivityItem['type'], string> = {
  saved:   'bg-gold/15 text-gold',
  alert:   'bg-red-500/15 text-red-400',
  download:'bg-teal/30 text-teal-light',
  viewed:  'bg-white/10 text-white/60',
  account: 'bg-white/10 text-white/60',
  bid:     'bg-green-500/15 text-green-400',
};
const STATE_PILL: Record<string, string> = {
  CA: 'bg-amber-500/15 text-amber-400',
  NV: 'bg-red-500/15 text-red-400',
  AZ: 'bg-orange-500/15 text-orange-400',
};

const QUICK_ACTIONS = [
  {
    state: 'CA',
    label: 'California',
    sub: 'CA Tax Foreclosures',
    href: 'https://businessdevelopmentauthority.net/ca-tax-foreclosures',
    color: 'bg-amber-500/10 border-amber-500/20 hover:border-amber-500/50',
    pill: 'bg-amber-500/20 text-amber-400',
    icon: 'ri-map-2-line',
    iconColor: 'text-amber-400',
    img: 'https://public.readdy.ai/ai/img_res/61b580def66b6eb07c9bdf187f19ed8f.jpg',
  },
  {
    state: 'NV',
    label: 'Nevada',
    sub: 'NV Tax Foreclosures',
    href: 'https://businessdevelopmentauthority.net/nv-tax-foreclosures',
    color: 'bg-red-500/10 border-red-500/20 hover:border-red-500/50',
    pill: 'bg-red-500/20 text-red-400',
    icon: 'ri-map-2-line',
    iconColor: 'text-red-400',
    img: 'https://readdy.ai/api/search-image?query=Nevada%20desert%20aerial%20view%20Las%20Vegas%20suburbs%20residential%20neighborhoods%20dry%20landscape%20real%20estate%20map%20view&width=300&height=160&seq=qa_nv&orientation=landscape',
  },
  {
    state: 'AZ',
    label: 'Arizona',
    sub: 'AZ Tax Foreclosures',
    href: 'https://businessdevelopmentauthority.net/tax-foreclosures',
    color: 'bg-orange-500/10 border-orange-500/20 hover:border-orange-500/50',
    pill: 'bg-orange-500/20 text-orange-400',
    icon: 'ri-map-2-line',
    iconColor: 'text-orange-400',
    img: 'https://readdy.ai/api/search-image?query=Arizona%20state%20aerial%20view%20Phoenix%20Scottsdale%20residential%20neighborhoods%20desert%20saguaro%20cactus%20warm%20tones%20real%20estate%20map%20view&width=300&height=160&seq=qa_az&orientation=landscape',
  },
];

const PLAN_COLORS: Record<string, string> = {
  Starter: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  Pro:     'bg-gold/20 text-gold border-gold/30',
  Elite:   'bg-white/10 text-white border-white/20',
};

interface Props {
  member: MemberProfile;
}

export default function OverviewContent({ member }: Props) {
  const stats = [
    {
      icon: 'ri-building-2-line',
      label: 'Active Deals',
      value: member.activeDeals,
      sub: 'In pipeline',
      color: 'text-gold',
      bg: 'bg-gold/10',
    },
    {
      icon: 'ri-map-pin-2-line',
      label: 'States Monitored',
      value: member.statesMonitored.length,
      sub: member.statesMonitored.join(' + '),
      color: 'text-green-400',
      bg: 'bg-green-400/10',
    },
    {
      icon: 'ri-bookmark-3-line',
      label: 'Saved Properties',
      value: member.savedCount,
      sub: '5 added this week',
      color: 'text-amber-400',
      bg: 'bg-amber-400/10',
    },
    {
      icon: 'ri-calendar-event-line',
      label: 'Next Auction',
      value: member.nextAuction,
      sub: 'Las Vegas, NV',
      color: 'text-red-400',
      bg: 'bg-red-400/10',
      isDate: true,
    },
  ];

  return (
    <div className="flex flex-col gap-8 min-w-0">

      {/* ── Welcome banner ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="font-playfair text-3xl font-bold text-white">
              Welcome back, {member.firstName}
            </h1>
            <span className="text-2xl select-none">👋</span>
          </div>
          <p className="text-white/45 text-sm">
            Here&apos;s what&apos;s happening across your portfolio today.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className={`px-3 py-1.5 text-xs font-bold rounded-full border ${PLAN_COLORS[member.plan]}`}>
            {member.plan} Plan
          </span>
          <span className="text-white/25 text-xs">Member since {member.memberSince}</span>
        </div>
      </div>

      {/* ── Stats cards ── */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <div key={i} className="bg-white/4 border border-white/8 rounded-2xl p-5">
            <div className="flex items-start justify-between mb-4">
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${s.bg}`}>
                <i className={`${s.icon} ${s.color} text-xl`} />
              </div>
              <i className="ri-arrow-up-line text-green-400 text-sm" />
            </div>
            <p className={`font-playfair font-bold leading-none mb-1 ${s.isDate ? 'text-xl' : 'text-4xl'} text-white`}>
              {s.value}
            </p>
            <p className="text-white/40 text-xs mt-2">{s.label}</p>
            <p className="text-white/25 text-xs mt-0.5">{s.sub}</p>
          </div>
        ))}
      </div>

      {/* ── Lower grid: Activity + Quick Actions ── */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">

        {/* Recent Activity */}
        <div className="xl:col-span-3 bg-white/4 border border-white/8 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-white font-semibold text-base">Recent Activity</h2>
            <span className="text-white/30 text-xs">Last 5 actions</span>
          </div>
          <ul className="flex flex-col divide-y divide-white/5">
            {recentActivity.map((item) => (
              <li key={item.id} className="flex items-start gap-4 py-4 first:pt-0 last:pb-0">
                {/* Icon */}
                <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 mt-0.5 ${ACTIVITY_COLORS[item.type]}`}>
                  <i className={`${ACTIVITY_ICONS[item.type]} text-sm`} />
                </div>
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-white/80 text-sm font-medium">{item.title}</p>
                    {item.state && (
                      <span className={`text-xs font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${STATE_PILL[item.state]}`}>
                        {item.state}
                      </span>
                    )}
                  </div>
                  <p className="text-white/40 text-xs leading-relaxed truncate">{item.detail}</p>
                </div>
                {/* Time */}
                <span className="text-white/25 text-xs flex-shrink-0 mt-0.5 whitespace-nowrap">{item.time}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Quick Actions */}
        <div className="xl:col-span-2 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h2 className="text-white font-semibold text-base">State Map Access</h2>
            <span className="text-white/30 text-xs">Opens externally</span>
          </div>
          {QUICK_ACTIONS.map((action) => (
            <a
              key={action.state}
              href={action.href}
              target="_blank"
              rel="noopener noreferrer"
              className={`group relative flex items-center gap-4 p-4 rounded-2xl border transition-all duration-200 overflow-hidden cursor-pointer ${action.color}`}
            >
              {/* Background image */}
              <div className="absolute inset-0 opacity-10 group-hover:opacity-20 transition-opacity duration-200">
                <img src={action.img} alt="" className="w-full h-full object-cover object-center" />
              </div>

              {/* Icon circle */}
              <div className="relative z-10 w-10 h-10 flex items-center justify-center rounded-xl bg-white/10 flex-shrink-0">
                <i className={`${action.icon} ${action.iconColor} text-xl`} />
              </div>

              {/* Label */}
              <div className="relative z-10 flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-white font-semibold text-sm">{action.label}</span>
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${action.pill}`}>
                    {action.state}
                  </span>
                </div>
                <p className="text-white/40 text-xs mt-0.5">{action.sub}</p>
              </div>

              {/* Arrow */}
              <div className="relative z-10 flex-shrink-0">
                <i className="ri-external-link-line text-white/30 group-hover:text-white/70 text-base transition-colors" />
              </div>
            </a>
          ))}

          {/* Upgrade nudge for non-elite */}
          {member.plan !== 'Elite' && (
            <div className="p-4 rounded-2xl border border-gold/20 bg-gold/5">
              <div className="flex items-start gap-3">
                <i className="ri-vip-crown-line text-gold text-xl flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white/80 text-sm font-semibold mb-1">Upgrade to Elite</p>
                  <p className="text-white/40 text-xs leading-relaxed">
                    Unlock 60–90 day early alerts, 1-on-1 coaching, and unlimited saved properties.
                  </p>
                  <a
                    href="/pricing"
                    className="inline-flex items-center gap-1 text-gold text-xs font-semibold mt-2 hover:text-gold-light transition-colors"
                  >
                    View upgrade options <i className="ri-arrow-right-line text-xs" />
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
